import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useResizableColumns } from '../hooks/useResizableColumns';
import VideoUploader from '../components/VideoUploader';
import ShareLinkManager from '../components/ShareLinkManager';

// ── Status config ─────────────────────────────────────────────────────────────

const PIPELINE_STEPS = ['Scripted', 'Editing', 'Qual', 'Approved', 'Posted'];

const AI_TRANSITIONS = {
  Scripted: ['Editing'],
  Editing:  ['Qual',     'Scripted'],
  Qual:     ['Approved', 'Editing'],
  Approved: ['Posted',   'Qual'],
  Posted:   ['Approved'],
};

const PILL_CLASS = {
  Scripted: 'pill-scripted',
  Editing:  'pill-editing',
  Qual:     'pill-qual',
  Approved: 'pill-approved',
  Posted:   'pill-posted',
};

const CHIP_STATUSES = ['Scripted', 'Editing', 'Qual', 'Approved', 'Posted'];
const ALL_STATUSES  = ['Scripted', 'Editing', 'Qual', 'Approved', 'Posted'];

// Default pixel widths for 7 data columns
const AI_COL_DEFAULTS = [280, 140, 130, 110, 120, 90, 220];

function formatDate(str) {
  if (!str) return '—';
  // Parse date-only strings as local date to avoid UTC offset shifting the day
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function toInputDate(str) {
  if (!str) return '';
  return typeof str === 'string' ? str.slice(0, 10) : new Date(str).toISOString().slice(0, 10);
}

// ── AiProduction page ─────────────────────────────────────────────────────────

export default function AiProduction() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [records,        setRecords]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [search,         setSearch]         = useState(() => searchParams.get('q') || '');
  const [statusFilter,   setStatusFilter]   = useState(() => searchParams.get('status') || '');
  const [clientFilter,   setClientFilter]   = useState(() => searchParams.get('client') || '');
  const [editorFilter,   setEditorFilter]   = useState(() => searchParams.get('editor') || '');
  const [clients,        setClients]        = useState([]);
  const [members,        setMembers]        = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [selectedCards,  setSelectedCards]  = useState(new Set());
  const [cardMassDelete, setCardMassDelete] = useState(false);
  const [ctxMenu,        setCtxMenu]        = useState(null); // { x, y, record }

  // Resizable columns
  const { template: aiColTemplate, startDrag: startAiColDrag } = useResizableColumns('colWidths_ai', AI_COL_DEFAULTS);

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (clientFilter) params.set('clientId', clientFilter);
      if (editorFilter) params.set('editorId', editorFilter);
      if (statusFilter === 'Posted') {
        params.set('status', 'Posted');
      } else if (statusFilter) {
        params.set('status', statusFilter);
      } else {
        params.set('excludePosted', 'true');
      }
      const res = await api.get(`/api/ai-production?${params}`);
      setRecords(res.data);
    } catch {
      setError('Failed to load records.');
    } finally {
      setLoading(false);
    }
  }, [clientFilter, statusFilter, editorFilter]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data)).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  // Deep-link from notification or ?card= URL param.
  // Keep ?card=<id> in the URL (mirrored from selectedRecord by the sync effect
  // below) so a refresh restores the open card instead of bouncing to the list.
  useEffect(() => {
    const openId = location.state?.openRecordId || searchParams.get('card');
    if (!openId || records.length === 0) return;
    if (selectedRecord?.id === openId) return; // already open — avoid reopen loops
    const rec = records.find(r => r.id === openId);
    if (rec) setSelectedRecord(rec);
    // Consume transient deep-link state but keep the ?card= param.
    if (location.state?.openRecordId) navigate('/ai' + location.search, { replace: true, state: {} });
  }, [location, records, navigate, selectedRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror the open card into the URL so a refresh restores it. Skip the initial
  // mount run so we don't strip ?card= before the deep-link effect above opens it.
  const cardUrlSyncInit = useRef(false);
  useEffect(() => {
    if (!cardUrlSyncInit.current) { cardUrlSyncInit.current = true; return; }
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (selectedRecord?.id) p.set('card', selectedRecord.id); else p.delete('card');
      return p;
    }, { replace: true });
  }, [selectedRecord?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ──────────────────────────────────────────────────────────────────

  const statusCounts = useMemo(() => {
    const c = {};
    records.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [records]);

  const filteredRecords = useMemo(() => {
    if (!search) return records;
    const q = search.toLowerCase();
    return records.filter(r =>
      r.script?.scriptName?.toLowerCase().includes(q) ||
      r.notes?.toLowerCase().includes(q) ||
      r.client?.name?.toLowerCase().includes(q) ||
      r.editor?.name?.toLowerCase().includes(q)
    );
  }, [records, search]);

  const [aiSort, setAiSort] = useState(() => ({ col: searchParams.get('sortBy') || '', dir: searchParams.get('sortDir') || 'asc' }));

  // Sync sort + filter state to URL params so browser back restores them
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (search) p.set('q', search); else p.delete('q');
      if (statusFilter) p.set('status', statusFilter); else p.delete('status');
      if (clientFilter) p.set('client', clientFilter); else p.delete('client');
      if (editorFilter) p.set('editor', editorFilter); else p.delete('editor');
      if (aiSort.col) p.set('sortBy', aiSort.col); else p.delete('sortBy');
      if (aiSort.dir !== 'asc') p.set('sortDir', aiSort.dir); else p.delete('sortDir');
      return p;
    }, { replace: true });
  }, [aiSort, search, statusFilter, clientFilter, editorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedRecords = useMemo(() => {
    if (!aiSort.col) return filteredRecords;
    const { col, dir } = aiSort;
    return [...filteredRecords].sort((a, b) => {
      let av = '', bv = '';
      if      (col === 'Script / Title') { av = a.script?.scriptName || ''; bv = b.script?.scriptName || ''; }
      else if (col === 'Project')        { av = a.client?.name || ''; bv = b.client?.name || ''; }
      else if (col === 'Editor')         { av = a.editor?.name || ''; bv = b.editor?.name || ''; }
      else if (col === 'Status')         { av = a.status || ''; bv = b.status || ''; }
      else if (col === 'Posted')         { av = a.postedDate || ''; bv = b.postedDate || ''; }
      else return 0;
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filteredRecords, aiSort]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  async function handleRowStatusChange(record, newStatus, e) {
    e.stopPropagation();
    setOpenDropdownId(null);
    try {
      const res = await api.patch(`/api/ai-production/${record.id}/status`, {
        status: newStatus, changedBy: user.id,
      });
      setRecords(prev => prev.map(r => r.id === record.id ? { ...r, ...res.data } : r));
    } catch (err) {
      alert(err.response?.data?.error || 'Status update failed');
    }
  }

  function handleRecordUpdated(updated) {
    setRecords(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
    setSelectedRecord(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
  }

  const isAdminOrPowerUser = user?.roles?.includes('Admin') || user?.roles?.includes('Power User');
  const isPostLead  = user?.roles?.includes('Post Lead') || user?.roles?.includes('Post_Lead');
  const isShootLead = user?.roles?.includes('Producer');
  const canMassDelete = isAdminOrPowerUser || isPostLead || isShootLead;
  const aiGridCols = canMassDelete ? `28px ${aiColTemplate}` : aiColTemplate;

  async function handleMassDeleteCards() {
    const ids = [...selectedCards];
    try {
      await Promise.all(ids.map(id => api.delete(`/api/ai-production/${id}`)));
      setRecords(prev => prev.filter(r => !selectedCards.has(r.id)));
      setSelectedCards(new Set());
      setCardMassDelete(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete one or more video cards.');
      setCardMassDelete(false);
      fetchRecords();
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">AI Production</div>
          <div className="page-sub">
            {records.length} video card{records.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="topbar-actions">
          {canMassDelete && selectedCards.size > 0 && (
            <button
              onClick={() => setCardMassDelete(true)}
              style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
            >
              Delete {selectedCards.size} card{selectedCards.size !== 1 ? 's' : ''}
            </button>
          )}
          <button className="btn-ghost" onClick={fetchRecords}>↺ Refresh</button>
        </div>
      </div>

      <div className="body" style={{ overflow: 'hidden', padding: '20px 36px 28px', gap: 14 }}>

        {/* Status chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
          {CHIP_STATUSES.map(s => (
            <div
              key={s}
              className={`chip chip-${s.toLowerCase()}${statusFilter && statusFilter !== s ? ' dimmed' : ''}`}
              onClick={() => setStatusFilter(prev => prev === s ? '' : s)}
            >
              <span>{s}</span>
              {statusCounts[s] != null && <span className="chip-count">{statusCounts[s]}</span>}
            </div>
          ))}
          {statusFilter && (
            <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setStatusFilter('')}>
              Clear ×
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <input
            className="filter-input"
            style={{ flex: 1, maxWidth: 280 }}
            placeholder="Search scripts…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="filter-input" style={{ width: 155 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="filter-input" style={{ width: 160 }} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
            <option value="">All projects</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="filter-input" style={{ width: 155 }} value={editorFilter} onChange={e => setEditorFilter(e.target.value)}>
            <option value="">All editors</option>
            {members.filter(m => m.roles?.some(r => r.role === 'Editor' || r.role === 'Power User')).map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {/* Head */}
          <div style={{ display: 'grid', gridTemplateColumns: aiGridCols, padding: '10px 22px', background: 'var(--bg-card2)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            {canMassDelete && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={sortedRecords.length > 0 && sortedRecords.every(r => selectedCards.has(r.id))}
                  onChange={e => {
                    if (e.target.checked) setSelectedCards(new Set(sortedRecords.map(r => r.id)));
                    else setSelectedCards(new Set());
                  }}
                  style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
                />
              </div>
            )}
            {['Script / Title', 'Project', 'Editor', 'Status', 'Thumb', 'Posted', 'Notes'].map((h, i) => {
              const sortable = !['Thumb', 'Notes'].includes(h);
              const active = aiSort.col === h;
              return (
                <div
                  key={h}
                  className="lp-th"
                  style={{ position: 'relative', ...(sortable ? { cursor: 'pointer', userSelect: 'none' } : {}) }}
                  onClick={sortable ? () => setAiSort(prev => ({ col: h, dir: prev.col === h && prev.dir === 'asc' ? 'desc' : 'asc' })) : undefined}
                >
                  {h}
                  {sortable && (
                    <span style={{ marginLeft: 4, fontSize: 9, opacity: active ? 1 : 0.25, color: active ? 'var(--violet)' : 'inherit' }}>
                      {active && aiSort.dir === 'desc' ? '▼' : '▲'}
                    </span>
                  )}
                  <div
                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 1 }}
                    onMouseDown={e => startAiColDrag(i, e)}
                  />
                </div>
              );
            })}
          </div>

          {/* Body */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {error ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--s-incomplete-t)' }}>{error}</div>
            ) : loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)' }}>Loading…</div>
            ) : filteredRecords.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)' }}>No records found</div>
            ) : sortedRecords.map(r => (
              <AiRow
                key={r.id}
                record={r}
                isDropdownOpen={openDropdownId === r.id}
                onOpenDropdown={e => { e.stopPropagation(); setOpenDropdownId(r.id); }}
                onStatusChange={(status, e) => handleRowStatusChange(r, status, e)}
                onClick={() => { if (openDropdownId) { setOpenDropdownId(null); return; } setSelectedRecord(r); }}
                onContextMenu={(e, rec) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, record: rec }); }}
                canSelect={canMassDelete}
                isSelected={selectedCards.has(r.id)}
                onSelect={() => setSelectedCards(prev => { const next = new Set(prev); if (next.has(r.id)) next.delete(r.id); else next.add(r.id); return next; })}
                gridCols={aiGridCols}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Dropdown overlay */}
      {openDropdownId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpenDropdownId(null)} />
      )}

      {/* Mass delete confirmation */}
      {cardMassDelete && (
        <div className="modal-overlay" onClick={() => setCardMassDelete(false)}>
          <div className="vc-modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="vc-header">
              <div><div className="vc-title">Delete {selectedCards.size} video card{selectedCards.size !== 1 ? 's' : ''}?</div></div>
              <button className="vc-close" onClick={() => setCardMassDelete(false)}>✕</button>
            </div>
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                Permanently delete <strong style={{ color: 'var(--tx-primary)' }}>{selectedCards.size} AI video card{selectedCards.size !== 1 ? 's' : ''}</strong>? Linked scripts will not be affected.
              </p>
            </div>
            <div className="vc-footer">
              <div />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setCardMassDelete(false)}>Cancel</button>
                <button
                  onClick={handleMassDeleteCards}
                  style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Card */}
      {selectedRecord && (
        <AiVideoCard
          record={selectedRecord}
          members={members}
          userId={user.id}
          onClose={() => setSelectedRecord(null)}
          onUpdated={handleRecordUpdated}
        />
      )}

      {/* Right-click context menu */}
      {ctxMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 299 }} onClick={() => setCtxMenu(null)} />
          <div
            style={{
              position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, zIndex: 300,
              background: 'var(--bg-card)', border: '1px solid var(--border-mid)',
              borderRadius: 8, padding: '4px 0', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              minWidth: 160,
            }}
            onClick={() => setCtxMenu(null)}
          >
            <div
              style={{ padding: '8px 14px', fontSize: 13, color: 'var(--tx-primary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,109,255,0.12)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => window.open(`${window.location.origin}/ai?card=${ctxMenu.record.id}`, '_blank')}
            >
              Open in new tab ↗
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── AI Grid Row ───────────────────────────────────────────────────────────────

function AiRow({ record, isDropdownOpen, onOpenDropdown, onStatusChange, onClick, onContextMenu, canSelect, isSelected, onSelect, gridCols }) {
  const transitions = AI_TRANSITIONS[record.status] || [];

  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: gridCols, background: isSelected ? 'rgba(99,149,255,0.07)' : undefined }}
      className="lp-row"
      onClick={onClick}
    >
      {canSelect && (
        <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onSelect}
            onClick={e => e.stopPropagation()}
            style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
          />
        </div>
      )}
      {/* Script */}
      <div style={{ minWidth: 0 }} onContextMenu={onContextMenu ? e => onContextMenu(e, record) : undefined}>
        <div className="script-name">{record.script?.scriptName || '—'}</div>
        {record.script?.inTranscript && (
          <div style={{ fontSize: 10, color: 'var(--violet)', marginTop: 2, fontWeight: 600 }}>
            IN TRANSCRIPT
          </div>
        )}
      </div>

      {/* Client */}
      <div className="lp-cell">{record.client?.name || '—'}</div>

      {/* Editor */}
      <div className={record.editor?.name ? 'lp-cell' : 'lp-cell-muted'}>
        {record.editor?.name || '—'}
      </div>

      {/* Status */}
      <div onClick={e => e.stopPropagation()}>
        {transitions.length > 0 ? (
          <div className="pill-select" onClick={onOpenDropdown}>
            <span className={`pill ${PILL_CLASS[record.status] || ''}`}>{record.status}</span>
            <span className="pill-chevron">▾</span>
          </div>
        ) : (
          <span className={`pill ${PILL_CLASS[record.status] || ''}`}>{record.status}</span>
        )}
        {isDropdownOpen && (
          <div className="status-dropdown">
            {transitions.map(t => (
              <div key={t} className="status-dropdown-item" onClick={e => onStatusChange(t, e)}>
                <span className={`pill ${PILL_CLASS[t] || ''}`}>{t}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail (or legacy frame link fallback) */}
      <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
        {record.videoThumbnailUrl
          ? <img src={record.videoThumbnailUrl} alt="" style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
          : record.frameLink
            ? <a className="frame-link" href={record.frameLink} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>↗</a>
            : <div style={{ width: 56, height: 32, background: 'var(--bg-input)', borderRadius: 4 }} />
        }
      </div>

      {/* Posted Date */}
      <div style={{ fontSize: 11, color: record.postedDate ? 'var(--s-approved-t)' : 'var(--tx-muted)', paddingTop: 3 }}>
        {record.postedDate ? formatDate(record.postedDate) : '—'}
      </div>

      {/* Notes */}
      <div className="lp-cell-muted" style={{ fontSize: 11 }}>
        {record.notes || '—'}
      </div>
    </div>
  );
}

// ── AI Video Card modal ───────────────────────────────────────────────────────

function AiVideoCard({ record: initialRecord, members, userId, onClose, onUpdated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [record,        setRecord]        = useState(initialRecord);
  const [form,          setForm]          = useState({
    editorId:   initialRecord.editorId   ?? '',
    frameLink:  initialRecord.frameLink  ?? '',
    postedDate: toInputDate(initialRecord.postedDate),
    notes:      initialRecord.notes      ?? '',
  });
  const [dirty,         setDirty]         = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [linkCopied,    setLinkCopied]    = useState(false);

  // Video asset state
  const [videoAsset,         setVideoAsset]         = useState(null);
  const [assetLoading,       setAssetLoading]       = useState(true);
  const [legacyLinkOpen,     setLegacyLinkOpen]     = useState(false);
  const [zipJob,             setZipJob]             = useState(null);
  const [deleteVideoConfirm, setDeleteVideoConfirm] = useState(false);
  const zipPollRef = useRef(null);

  function setField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  }

  const editors     = members.filter(m => m.roles?.some(r => r.role === 'Editor' || r.role === 'Power User'));
  const pipelineIdx = PIPELINE_STEPS.indexOf(record.status);
  const transitions = AI_TRANSITIONS[record.status] || [];

  const isAdmin     = user?.roles?.includes('Admin') || user?.roles?.includes('Power User');
  const isShootLead = user?.roles?.includes('Producer');
  const isEditor    = user?.roles?.includes('Editor');
  const canApprove  = isAdmin || user?.roles?.includes('Qual Reviewer');
  const canEditVideoCard   = isAdmin || isEditor || isShootLead;
  const visibleTransitions = transitions.filter(t => t !== 'Approved' || canApprove);

  // Fetch video asset for this card
  useEffect(() => {
    api.get(`/api/video-assets?aiProductionId=${record.id}`)
      .then(r => setVideoAsset(r.data))
      .catch(() => {})
      .finally(() => setAssetLoading(false));
  }, [record.id]);

  // Clean up zip poll on unmount
  useEffect(() => {
    return () => { if (zipPollRef.current) clearInterval(zipPollRef.current); };
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await api.patch(`/api/ai-production/${record.id}`, {
        editorId:   form.editorId   || null,
        frameLink:  form.frameLink  || null,
        postedDate: form.postedDate || null,
        notes:      form.notes      || null,
      });
      const updated = { ...record, ...res.data };
      setRecord(updated);
      setDirty(false);
      onUpdated(updated);
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusChange(newStatus) {
    setTransitioning(true);
    try {
      const res = await api.patch(`/api/ai-production/${record.id}/status`, {
        status: newStatus, changedBy: userId,
      });
      const updated = { ...record, ...res.data };
      setRecord(updated);
      onUpdated(updated);
    } catch (err) {
      alert(err.response?.data?.error || 'Status change failed');
    } finally {
      setTransitioning(false);
    }
  }

  async function handleDownload() {
    try {
      const res = await api.get(`/api/video-assets/${videoAsset.id}/download`);
      // Force a direct download (the API returns a presigned URL with
      // Content-Disposition: attachment) instead of opening the inline player.
      const a = document.createElement('a');
      a.href = res.data.url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      alert('Download failed. Please try again.');
    }
  }

  async function handleZipDownload() {
    try {
      setZipJob({ status: 'processing' });
      const res = await api.post('/api/downloads/zip', { videoAssetIds: [videoAsset.id] });
      const { jobId } = res.data;
      zipPollRef.current = setInterval(async () => {
        try {
          const poll = await api.get(`/api/downloads/zip/${jobId}`);
          if (poll.data.status === 'complete') {
            clearInterval(zipPollRef.current);
            setZipJob({ status: 'complete', downloadUrl: poll.data.downloadUrl });
          } else if (poll.data.status === 'failed') {
            clearInterval(zipPollRef.current);
            setZipJob(null);
            alert('Zip generation failed. Please try again.');
          }
        } catch {
          clearInterval(zipPollRef.current);
          setZipJob(null);
        }
      }, 3000);
    } catch {
      setZipJob(null);
      alert('Failed to start zip download. Please try again.');
    }
  }

  async function handleDeleteVideo() {
    try {
      await api.delete(`/api/video-assets/${videoAsset.id}`);
      setDeleteVideoConfirm(false);
      setVideoAsset(null);
    } catch (err) {
      setDeleteVideoConfirm(false);
      alert(err.response?.data?.error || 'Failed to delete video.');
    }
  }

  function transitionLabel(status, isBack) {
    if (isBack) return `← Back to ${status}`;
    const labels = { Editing: 'Send to Editing →', Qual: 'Send to Qual →', Approved: 'Approve ✓', Posted: 'Mark as Posted →' };
    return labels[status] || status;
  }

  const COL_LABEL = {
    narration: 'Narration', visuals: 'Visuals',
    character: 'Character', line: 'Line', notes: 'Notes',
    scene: 'Scene Description', props: 'Props',
    aRoll: 'A Roll', bRoll: 'B Roll', vfx: 'VFX', sfx: 'SFX',
  };

  async function handleExportCSV() {
    if (!record.script?.id) { alert('No script linked to this video card.'); return; }
    try {
      const res    = await api.get(`/api/scripts/${record.script.id}`);
      const script = res.data;
      const scenes = script.scriptBody?.scenes || [];
      if (scenes.length === 0) { alert('No scene data to export.'); return; }
      const keys   = Object.keys(scenes[0]).filter(k => !k.startsWith('_'));
      const esc    = v => `"${String(v || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
      const header = keys.map(k => esc(COL_LABEL[k] || k)).join(',');
      const rows   = scenes.map(row => keys.map(k => esc(row[k] || '')).join(','));
      const csv    = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${script.scriptName}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export script.');
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="vc-header">
          <div style={{ minWidth: 0 }}>
            <div className="vc-label">Video Card · AI Production</div>
            <div className="vc-title">{record.script?.scriptName || '—'}</div>
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className={`pill ${PILL_CLASS[record.status] || ''}`}>{record.status}</span>
              <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>{record.client?.name}</span>
              {record.script?.id && (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 11, padding: '3px 10px' }}
                  onClick={() => { onClose(); navigate('/scripts', { state: { openScriptId: record.script.id, fromVideoCard: true, isShootLeadForScript: isShootLead } }); }}
                >
                  View Script ↗
                </button>
              )}
              <button
                type="button"
                className="btn-ghost"
                style={{ fontSize: 11, padding: '3px 10px' }}
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/ai?card=${record.id}`);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                }}
              >
                {linkCopied ? 'Copied!' : '⎘ Copy Link'}
              </button>
            </div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        {/* Pipeline */}
        <div className="vc-pipeline">
          {PIPELINE_STEPS.map((step, idx) => {
            let cls = '';
            if (idx < pipelineIdx)        cls = 'done';
            else if (idx === pipelineIdx)  cls = 'current';
            return (
              <div key={step} className={`pp-step ${cls}`}>
                <div className="pp-dot" />
                <div className="pp-label">{step}</div>
              </div>
            );
          })}
        </div>

        {/* Form */}
        <div className="vc-body">

          {/* ── Video section ── */}
          <div className="vc-field full">
            <label>Video</label>
            {assetLoading ? (
              <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>Loading…</div>
            ) : videoAsset ? (
              <div
                onClick={() => {
                  const p = new URLSearchParams(location.search);
                  p.set('card', record.id);
                  navigate(`/review/${videoAsset.id}`, { state: { returnTo: `${location.pathname}?${p.toString()}` } });
                }}
                style={{ position: 'relative', width: '100%', maxWidth: 480, borderRadius: 10, overflow: 'hidden', background: '#000', aspectRatio: '16/9', cursor: 'pointer' }}
              >
                {videoAsset.thumbnailUrl ? (
                  <img src={videoAsset.thumbnailUrl} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-card2)' }}>
                    <span style={{ fontSize: 11, color: 'var(--tx-faint)' }}>Thumbnail generating…</span>
                  </div>
                )}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '10px 0 10px 18px', borderColor: 'transparent transparent transparent #fff', marginLeft: 4 }} />
                  </div>
                </div>
                {(videoAsset.openCommentCount ?? 0) > 0 && (
                  <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 4, padding: '2px 7px', fontSize: 10, color: '#fff' }}>
                    {videoAsset.openCommentCount} open
                  </div>
                )}
                <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 4, padding: '2px 7px', fontSize: 10, color: '#fff', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {videoAsset.fileName}
                </div>
              </div>
            ) : canEditVideoCard ? (
              <VideoUploader
                clientId={record.clientId}
                aiProductionId={record.id}
                onUploadComplete={asset => setVideoAsset(asset)}
              />
            ) : (
              <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>No video uploaded yet.</div>
            )}
          </div>

          {/* ── Download section (A7) ── */}
          {videoAsset && (
            <div className="vc-field full" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ width: '100%' }}>Download</label>
              <button className="btn-ghost" style={{ fontSize: 12 }} onClick={handleDownload}>
                ↓ Download Video
              </button>
              {zipJob?.status !== 'complete' && (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12 }}
                  disabled={zipJob?.status === 'processing'}
                  onClick={handleZipDownload}
                >
                  {zipJob?.status === 'processing' ? 'Preparing zip…' : '⬇ Download as Zip'}
                </button>
              )}
              {zipJob?.status === 'complete' && zipJob.downloadUrl && (
                <a
                  href={zipJob.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                  style={{ fontSize: 12, textDecoration: 'none', padding: '8px 14px', borderRadius: 'var(--radius-sm)' }}
                >
                  ↓ Download Zip
                </a>
              )}
              {(canEditVideoCard || videoAsset.uploadedBy === user?.id) && (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12, color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.35)' }}
                  onClick={() => setDeleteVideoConfirm(true)}
                >
                  Archive Video
                </button>
              )}
            </div>
          )}

          <div className="vc-field">
            <label>Editor</label>
            <select className="vc-input" value={form.editorId} onChange={e => setField('editorId', e.target.value)} disabled={!canEditVideoCard}>
              <option value="">— Not assigned —</option>
              {editors.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>

          <div className="vc-field">
            <label>Script</label>
            <input className="vc-input" readOnly value={record.script?.scriptName || '—'} />
          </div>

          {/* ── Frame Review Link — collapsible legacy if video asset exists ── */}
          {videoAsset ? (
            <div className="vc-field full">
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--tx-faint)', padding: 0, fontFamily: 'var(--font)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: legacyLinkOpen ? 8 : 0 }}
                onClick={() => setLegacyLinkOpen(v => !v)}
              >
                <span>{legacyLinkOpen ? '▾' : '▸'}</span> Legacy Frame.io link
              </button>
              {legacyLinkOpen && canEditVideoCard && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input className="vc-input" style={{ flex: 1 }} value={form.frameLink} onChange={e => setField('frameLink', e.target.value)} placeholder="https://app.frame.io/…" />
                  {form.frameLink?.startsWith('http') && (
                    <a href={form.frameLink} target="_blank" rel="noreferrer"
                      style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--violet)', color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      ↗ Open
                    </a>
                  )}
                </div>
              )}
              {legacyLinkOpen && !canEditVideoCard && form.frameLink?.startsWith('http') && (
                <a href={form.frameLink} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 'var(--radius-sm)', background: 'var(--violet)', color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                  Review Video ↗
                </a>
              )}
            </div>
          ) : (
            <div className="vc-field full">
              <label>Frame Review Link</label>
              {canEditVideoCard ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input className="vc-input" style={{ flex: 1 }} value={form.frameLink} onChange={e => setField('frameLink', e.target.value)} placeholder="https://app.frame.io/…" />
                  {form.frameLink?.startsWith('http') && (
                    <a href={form.frameLink} target="_blank" rel="noreferrer"
                      style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--violet)', color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      ↗ Open
                    </a>
                  )}
                </div>
              ) : form.frameLink?.startsWith('http') ? (
                <a href={form.frameLink} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 'var(--radius-sm)', background: 'var(--violet)', color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                  Review Video ↗
                </a>
              ) : (
                <span style={{ fontSize: 13, color: 'var(--tx-faint)' }}>—</span>
              )}
            </div>
          )}

          <div className="vc-field">
            <label>Posted Date</label>
            <input className="vc-input" type="date" value={form.postedDate} onChange={e => setField('postedDate', e.target.value)} disabled={!canEditVideoCard} />
          </div>

          <div className="vc-field full">
            <label>Notes</label>
            <textarea className="vc-input" value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Internal notes…" readOnly={!canEditVideoCard} />
          </div>

          {/* Share Links (C4) */}
          {videoAsset && <ShareLinkManager videoAssetId={videoAsset.id} />}

        </div>

        {/* Footer */}
        <div className="vc-footer">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {visibleTransitions.map(t => {
              const isBack = PIPELINE_STEPS.indexOf(t) < pipelineIdx;
              return (
                <button
                  key={t}
                  className={t === 'Approved' && !isBack ? 'btn-primary' : 'btn-ghost'}
                  style={isBack ? { color: 'var(--tx-secondary)', fontSize: 12, borderStyle: 'dashed' } : undefined}
                  onClick={() => handleStatusChange(t)}
                  disabled={transitioning}
                >
                  {transitionLabel(t, isBack)}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {record.script?.id && (
              <button className="btn-ghost" onClick={handleExportCSV} title="Download script as CSV">
                ↓ Export CSV
              </button>
            )}
            {dirty && (
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            )}
            <button className="btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>

      </div>

      {/* R-2 (Context.HeliconflowReviewUploadRestore.md): removing a video always archives it */}
      {deleteVideoConfirm && (() => {
        return (
          <div className="modal-overlay" onClick={() => setDeleteVideoConfirm(false)}>
            <div className="vc-modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
              <div className="vc-header">
                <div><div className="vc-title">Archive Video?</div></div>
                <button className="vc-close" onClick={() => setDeleteVideoConfirm(false)}>✕</button>
              </div>
              <div style={{ padding: '20px 28px' }}>
                <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                  <strong style={{ color: 'var(--tx-primary)' }}>{videoAsset?.fileName}</strong> will be <strong style={{ color: 'var(--tx-secondary)' }}>archived</strong> — never deleted from storage.
                </p>
                <p style={{ fontSize: 12, color: 'var(--tx-faint)', lineHeight: 1.6, margin: '10px 0 0' }}>
                  The file and any comments are kept. Archived versions are listed in the Video Review page's version menu, where they can be restored. Only an Admin can permanently delete an archive.
                </p>
              </div>
              <div className="vc-footer">
                <div />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-ghost" onClick={() => setDeleteVideoConfirm(false)}>Cancel</button>
                  <button
                    onClick={handleDeleteVideo}
                    style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                  >
                    Archive Video
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
